import { SPROUT_AGENT } from '@fruitful/llm-service-shared';
import { useRouter } from 'expo-router';
import { Column } from 'primereact/column';
import { DataTable, type DataTableRowClickEvent } from 'primereact/datatable';
import { type ReactNode, useMemo, useState } from 'react';
import {
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ChevronDownIcon,
    H6Emphasized,
    HistoryIcon,
    IconButton,
    Input,
    LabelRegular,
    MultilineInput,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import { formatToSimpleDateTimeString } from '../../common/utils/formatting';
import { useCreateQASuite, useQASuites } from '../hooks/useQASuite';
import {
    computeBatchSummaries,
    isCustomPromptName,
    type QABatchSummary,
} from './sproutQASimulationThreads';
import { useSproutQASimulationThreads } from './useSproutQASimulationThreads';

interface BatchListRow {
    summary: QABatchSummary;
    inProgress: boolean;
}

const renderRan = ({ summary, inProgress }: BatchListRow): ReactNode =>
    inProgress ? (
        <Spinner />
    ) : (
        <BodySmallRegular>
            {formatToSimpleDateTimeString(summary.ranAt)}
        </BodySmallRegular>
    );

const renderPrompt = ({ summary, inProgress }: BatchListRow): ReactNode => {
    if (inProgress) return null;
    return isCustomPromptName(summary.promptName) ? (
        <BodySmallRegular>{summary.promptName}</BodySmallRegular>
    ) : (
        <BodySmallRegular color="$gray700">Default</BodySmallRegular>
    );
};

const renderTests = ({ summary, inProgress }: BatchListRow): ReactNode =>
    inProgress ? null : (
        <BodySmallRegular>{String(summary.testCount)}</BodySmallRegular>
    );

const renderResult = ({ summary, inProgress }: BatchListRow): ReactNode =>
    inProgress ? null : (
        <XStack alignItems="center" justifyContent="center" gap={4}>
            <BodySmallRegular color="$green900" fontWeight="700">
                {String(summary.passed)}
            </BodySmallRegular>
            <BodySmallRegular color="$gray500">/</BodySmallRegular>
            <BodySmallRegular color="$red900" fontWeight="700">
                {String(summary.failed)}
            </BodySmallRegular>
        </XStack>
    );

const RunSplitButton = ({
    onRunDefault,
    onConfigure,
    disabled,
}: {
    onRunDefault: () => void;
    onConfigure: () => void;
    disabled: boolean;
}) => (
    <XStack alignItems="stretch">
        <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SMALL}
            width="auto"
            text="Run suite"
            onPress={onRunDefault}
            disabled={disabled}
            borderTopRightRadius={0}
            borderBottomRightRadius={0}
        />
        <XStack width={1} backgroundColor="$gray700" />
        <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SMALL}
            width="auto"
            text=""
            icon={ChevronDownIcon}
            onPress={onConfigure}
            disabled={disabled}
            borderTopLeftRadius={0}
            borderBottomLeftRadius={0}
            paddingHorizontal={10}
            aria-label="Configure run"
        />
    </XStack>
);

export default function SproutQARunsListPage() {
    const router = useRouter();
    const createSuite = useCreateQASuite();

    const [showConfigModal, setShowConfigModal] = useState(false);
    const [promptName, setPromptName] = useState('');
    const [promptInstructions, setPromptInstructions] = useState('');

    const {
        data: threads = [],
        isLoading,
        error,
    } = useSproutQASimulationThreads();
    const {
        data: serviceSuites = [],
        isLoading: isLoadingSuites,
        error: suitesError,
    } = useQASuites(SPROUT_AGENT.id);

    const rows: BatchListRow[] = useMemo(() => {
        const persisted = computeBatchSummaries(threads);
        const persistedById = new Map(
            persisted.map((summary) => [summary.batchId, summary]),
        );
        const serviceRows = serviceSuites.map((suite) => {
            const stored = persistedById.get(suite.id);
            return {
                summary: {
                    batchId: suite.id,
                    promptName: suite.promptName ?? null,
                    ranAt: new Date(suite.createdAt),
                    testCount: suite.total,
                    passed: stored?.passed ?? 0,
                    failed: stored?.failed ?? 0,
                },
                inProgress:
                    suite.status === 'pending' || suite.status === 'running',
            };
        });
        const serviceIds = new Set(serviceSuites.map(({ id }) => id));
        return [
            ...serviceRows,
            ...persisted
                .filter(({ batchId }) => !serviceIds.has(batchId))
                .map((summary) => ({ summary, inProgress: false })),
        ];
    }, [serviceSuites, threads]);

    const launchBatch = async (
        name: string | null,
        instructions: string | null,
    ): Promise<void> => {
        const { suiteId } = await createSuite.mutateAsync({
            agentId: SPROUT_AGENT.id,
            promptName: name ?? undefined,
            promptInstructions: instructions ?? undefined,
        });
        router.push({
            pathname: '/sprout-qa/[batchId]',
            params: { batchId: suiteId },
        });
    };

    const handleRunDefault = (): void => {
        void launchBatch(null, null).catch(() => undefined);
    };

    const handleRunConfigured = (): void => {
        const name = promptName.trim();
        const instructions = promptInstructions.trim();
        setShowConfigModal(false);
        void launchBatch(
            name === '' ? null : name,
            instructions === '' ? null : instructions,
        ).catch(() => undefined);
    };

    const handleOpenHistory = (): void => {
        router.push('/sprout-qa/history');
    };

    if (isLoading || isLoadingSuites) {
        return (
            <XStack justifyContent="center" padding="$4">
                <Spinner />
            </XStack>
        );
    }

    if (error != null || suitesError != null) {
        return (
            <YStack padding="$4">
                <BodySmallRegular color="$red1100">
                    Unable to load batch runs.
                </BodySmallRegular>
            </YStack>
        );
    }

    return (
        <YStack
            flex={1}
            minHeight={0}
            height="100%"
            width="100%"
            gap={12}
            padding={8}
            overflow="hidden"
        >
            <XStack alignItems="center" gap={12} flexWrap="wrap">
                <H6Emphasized marginRight="auto">Runs</H6Emphasized>
                <RunSplitButton
                    onRunDefault={handleRunDefault}
                    disabled={createSuite.isPending}
                    onConfigure={() => {
                        setShowConfigModal(true);
                    }}
                />
                <IconButton
                    variant="secondary"
                    icon={HistoryIcon}
                    onPress={handleOpenHistory}
                />
            </XStack>

            <GroveSimpleModal
                isOpen={showConfigModal}
                onClose={() => {
                    setShowConfigModal(false);
                }}
                hasCloseButton
            >
                <YStack gap={16} padding={24} width={560}>
                    <H6Emphasized>Configure run</H6Emphasized>
                    <YStack gap="$1">
                        <LabelRegular color="$gray1000">
                            Prompt name (optional)
                        </LabelRegular>
                        <Input
                            size="regular"
                            value={promptName}
                            onChangeText={setPromptName}
                            placeholder="e.g. Sprout prompt v3"
                        />
                    </YStack>
                    <YStack gap="$1">
                        <LabelRegular color="$gray1000">
                            Prompt instructions to test (optional)
                        </LabelRegular>
                        <MultilineInput
                            value={promptInstructions}
                            onChangeText={setPromptInstructions}
                            numberOfLines={8}
                            placeholder="Paste prospective agent prompt/instructions here..."
                            width="100%"
                        />
                    </YStack>
                    <XStack justifyContent="flex-end" gap={12}>
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            width="auto"
                            text="Cancel"
                            onPress={() => {
                                setShowConfigModal(false);
                            }}
                        />
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.SMALL}
                            width="auto"
                            text="Run suite"
                            onPress={handleRunConfigured}
                        />
                    </XStack>
                </YStack>
            </GroveSimpleModal>

            <YStack flex={1} minHeight={0}>
                <DataTable
                    value={rows}
                    dataKey="summary.batchId"
                    size="small"
                    showGridlines
                    stripedRows
                    scrollable
                    scrollHeight="flex"
                    tableStyle={{ width: '100%' }}
                    emptyMessage="No batch runs yet. Run the suite to get started."
                    rowClassName={() => 'qa-batch-list-row'}
                    onRowClick={(
                        event: DataTableRowClickEvent & { data: BatchListRow },
                    ) => {
                        const rowData = event.data;

                        router.push({
                            pathname: '/sprout-qa/[batchId]',
                            params: { batchId: rowData.summary.batchId },
                        });
                    }}
                >
                    <Column
                        field="summary.ranAt"
                        header="Run"
                        body={renderRan}
                        style={{ width: '28%' }}
                    />
                    <Column
                        field="summary.promptName"
                        header="Prompt"
                        body={renderPrompt}
                        style={{ width: '34%' }}
                    />
                    <Column
                        field="summary.testCount"
                        header="Tests"
                        body={renderTests}
                        alignHeader="center"
                        style={{ width: '14%', textAlign: 'center' }}
                    />
                    <Column
                        field="summary.passed"
                        header="Result"
                        body={renderResult}
                        alignHeader="center"
                        style={{ width: '24%', textAlign: 'center' }}
                    />
                </DataTable>
                <style>{`
                    .p-datatable .qa-batch-list-row {
                        cursor: pointer;
                    }
                    .p-datatable .qa-batch-list-row:hover {
                        background-color: #E1FDEA !important;
                    }
                `}</style>
            </YStack>
        </YStack>
    );
}
